import { Context, internal, LDFlagValue, LDLogger, Platform } from '@launchdarkly/js-sdk-common';

import { LDDebugOverride } from '../api/LDDebugOverride';
import { namespaceForEnvironment } from '../storage/namespaceUtils';
import FlagPersistence from './FlagPersistence';
import { createDefaultFlagStore } from './FlagStore';
import createFlagUpdater, { FlagsChangeCallback, FlagUpdater } from './FlagUpdater';
import { ItemDescriptor } from './ItemDescriptor';

/**
 * Top level manager of flags for the client. LDClient should be using this
 * interface and not any of the specific instances managed by it. Updates from
 * data sources should be directed to the [init] and [upsert] methods of this
 * interface.
 */
export interface FlagManager {
  /**
   * Attempts to get a flag by key from the current flags.
   */
  get(key: string): ItemDescriptor | undefined;

  /**
   * Gets all the current flags.
   */
  getAll(): { [key: string]: ItemDescriptor };

  /**
   * Initializes the flag manager with data from a data source.
   * Persistence initialization is handled by {@link FlagPersistence}
   */
  init(context: Context, newFlags: { [key: string]: ItemDescriptor }): Promise<void>;

  /**
   * Attempt to update a flag. If the flag is for the wrong context, or
   * it is of an older version, then an update will not be performed.
   */
  upsert(context: Context, key: string, item: ItemDescriptor): Promise<boolean>;

  /**
   * Asynchronously load cached values from persistence.
   */
  loadCached(context: Context): Promise<boolean>;

  /**
   * Updates in-memory storage with the specified flags without a context
   * or persistent storage. Flags set in this way are considered emphemeral and
   * should be replaced as soon as initialization is done.
   *
   * @param newFlags - cached flags
   */
  presetFlags(newFlags: { [key: string]: ItemDescriptor }): void;

  /**
   * Update in-memory storage with the specified flags, but do not persistent them to cache
   * storage.
   */
  setBootstrap(context: Context, newFlags: { [key: string]: ItemDescriptor }): void;

  /**
   * Applies a changeset to the flag store.
   * - `'full'`: replaces all flags (like {@link init}).
   * - `'partial'`: upserts individual flags (like calling {@link upsert} for each entry).
   * - `'none'`: persists cache (updating freshness) without changing any flags.
   *
   * Designed for the FDv2 data path where init/upsert semantics, selector
   * tracking, and freshness updates are all handled in one call.
   */
  applyChanges(
    context: Context,
    updates: { [key: string]: ItemDescriptor },
    type: internal.PayloadType,
  ): Promise<void>;

  /**
   * Register a flag change callback.
   */
  on(callback: FlagsChangeCallback): void;

  /**
   * Unregister a flag change callback.
   */
  off(callback: FlagsChangeCallback): void;

  /**
   * Obtain debug override functions that allows plugins
   * to manipulate the outcome of the flags managed by
   * this manager
   *
   * @experimental This function is experimental and intended for use by LaunchDarkly tools at this time.
   */
  getDebugOverride?(): LDDebugOverride;
}

export default class DefaultFlagManager implements FlagManager {
  private _flagStore = createDefaultFlagStore();
  private _flagUpdater: FlagUpdater;
  private _flagPersistencePromise: Promise<FlagPersistence>;
  private _overrides?: { [key: string]: LDFlagValue };

  /**
   * @param platform implementation of various platform provided functionality
   * @param sdkKey that will be used to distinguish different environments
   * @param maxCachedContexts that specifies the max number of contexts that will be cached in persistence
   * @param disableCache set to true to completely disable the persistent flag cache
   * @param logger used for logging various messages
   * @param timeStamper exists for testing purposes
   */
  constructor(
    platform: Platform,
    sdkKey: string,
    maxCachedContexts: number,
    disableCache: boolean,
    logger: LDLogger,
    timeStamper: () => number = () => Date.now(),
  ) {
    this._flagUpdater = createFlagUpdater(this._flagStore, logger);
    this._flagPersistencePromise = this._initPersistence(
      platform,
      sdkKey,
      maxCachedContexts,
      disableCache,
      logger,
      timeStamper,
    );
  }

  private async _initPersistence(
    platform: Platform,
    sdkKey: string,
    maxCachedContexts: number,
    disableCache: boolean,
    logger: LDLogger,
    timeStamper: () => number = () => Date.now(),
  ): Promise<FlagPersistence> {
    const environmentNamespace = await namespaceForEnvironment(platform.crypto, sdkKey);

    return new FlagPersistence(
      platform,
      environmentNamespace,
      maxCachedContexts,
      disableCache,
      this._flagStore,
      this._flagUpdater,
      logger,
      timeStamper,
    );
  }

  get(key: string): ItemDescriptor | undefined {
    if (this._overrides && Object.prototype.hasOwnProperty.call(this._overrides, key)) {
      return this._convertValueToOverrideDescripter(this._overrides[key]);
    }

    return this._flagStore.get(key);
  }

  getAll(): { [key: string]: ItemDescriptor } {
    if (this._overrides) {
      return {
        ...this._flagStore.getAll(),
        ...Object.entries(this._overrides).reduce(
          (acc: { [key: string]: ItemDescriptor }, [key, value]) => {
            acc[key] = this._convertValueToOverrideDescripter(value);
            return acc;
          },
          {},
        ),
      };
    }
    return this._flagStore.getAll();
  }

  presetFlags(newFlags: { [key: string]: ItemDescriptor }): void {
    this._flagStore.init(newFlags);
  }

  setBootstrap(context: Context, newFlags: { [key: string]: ItemDescriptor }): void {
    // Bypasses the persistence as we do not want to put these flags into any cache.
    // Generally speaking persistence likely *SHOULD* be disabled when using bootstrap.
    this._flagUpdater.init(context, newFlags);
  }

  async init(context: Context, newFlags: { [key: string]: ItemDescriptor }): Promise<void> {
    return (await this._flagPersistencePromise).init(context, newFlags);
  }

  async upsert(context: Context, key: string, item: ItemDescriptor): Promise<boolean> {
    return (await this._flagPersistencePromise).upsert(context, key, item);
  }

  async loadCached(context: Context): Promise<boolean> {
    return (await this._flagPersistencePromise).loadCached(context);
  }

  async applyChanges(
    context: Context,
    updates: { [key: string]: ItemDescriptor },
    type: internal.PayloadType,
  ): Promise<void> {
    return (await this._flagPersistencePromise).applyChanges(context, updates, type);
  }

  on(callback: FlagsChangeCallback): void {
    this._flagUpdater.on(callback);
  }

  off(callback: FlagsChangeCallback): void {
    this._flagUpdater.off(callback);
  }

  private _convertValueToOverrideDescripter(value: LDFlagValue): ItemDescriptor {
    return {
      flag: {
        value,
        version: 0,
      },
      version: 0,
    };
  }

  setOverride(key: string, value: LDFlagValue) {
    if (!this._overrides) {
      this._overrides = {};
    }
    this._overrides[key] = value;
    this._flagUpdater.handleFlagChanges([key], 'override');
  }

  removeOverride(flagKey: string) {
    if (!this._overrides || !Object.prototype.hasOwnProperty.call(this._overrides, flagKey)) {
      return; // No override to remove
    }

    delete this._overrides[flagKey];

    // If no more overrides, reset to undefined for performance
    if (Object.keys(this._overrides).length === 0) {
      this._overrides = undefined;
    }

    this._flagUpdater.handleFlagChanges([flagKey], 'override');
  }

  clearAllOverrides() {
    if (this._overrides) {
      const clearedOverrides = { ...this._overrides };
      this._overrides = undefined; // Reset to undefined
      this._flagUpdater.handleFlagChanges(Object.keys(clearedOverrides), 'override');
    }
  }

  getAllOverrides() {
    if (!this._overrides) {
      return {};
    }
    const result = {} as { [key: string]: ItemDescriptor };
    Object.entries(this._overrides).forEach(([key, value]) => {
      result[key] = this._convertValueToOverrideDescripter(value);
    });
    return result;
  }

  getDebugOverride(): LDDebugOverride {
    return {
      setOverride: this.setOverride.bind(this),
      removeOverride: this.removeOverride.bind(this),
      clearAllOverrides: this.clearAllOverrides.bind(this),
      getAllOverrides: this.getAllOverrides.bind(this),
    };
  }
}
